import { db } from './firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { getHolesByCourse, getBergvlietCourseHandicap } from './courseData';

// Computes the tournament-style Season Finale leaderboard for every approved
// season player (not just those who've started a finale round). Bonus strokes
// come from finaleConfig.bonusAssignments, which is locked in at event-creation
// time (see AdminPanel.js) — recomputing season positions live here would drift
// from the bonus each player's finaleRound was actually scored with in
// ScoreEntry.js. Mirrors the computation in App.js so both surfaces agree.
export async function computeFinaleLeaderboard({
  todayFinaleEvent,
  activeSeason,
  finaleRounds,
  users,
  getUserName,
}) {
  const finaleConfig = todayFinaleEvent?.finaleConfig || {};
  const bonusAssignments = finaleConfig?.bonusAssignments || {};
  const nonParticipants = todayFinaleEvent?.nonParticipants || [];
  const bergvlietHoles = getHolesByCourse('Bergvliet');

  let recentHandicapMap = {};
  try {
    if (activeSeason?.id) {
      const roundsSnap = await getDocs(query(
        collection(db, 'rounds'),
        where('seasonId', '==', activeSeason.id),
        where('status', '==', 'approved')
      ));
      const sortedRounds = roundsSnap.docs
        .map((d) => d.data())
        .filter((r) => !r?.isFinale)
        .sort((a, b) => new Date(b?.date) - new Date(a?.date));

      sortedRounds.forEach((round) => {
        if (!recentHandicapMap[round?.userId] && round?.handicap) {
          recentHandicapMap[round.userId] = round.handicap;
        }
      });
    }
  } catch (err) {
    console.error('Error fetching season rounds for finale handicaps:', err);
  }

  let approvedUsers = [];
  try {
    if (users) {
      approvedUsers = Object.entries(users).filter(([, u]) => u?.status === 'approved');
    } else {
      const usersSnap = await getDocs(collection(db, 'users'));
      approvedUsers = usersSnap.docs
        .map((d) => [d.id, d.data()])
        .filter(([, u]) => u?.status === 'approved');
    }
  } catch (err) {
    console.error('Error fetching users for finale leaderboard:', err);
  }

  const computed = approvedUsers.map(([userId, u]) => {
    const finaleRound = (finaleRounds || []).find((r) => r?.userId === userId);
    const bonusStrokes = bonusAssignments?.[userId] || 0;

    let handicapIndex;
    let courseHandicap;
    let teeboxColor;

    if (finaleRound && finaleRound.holesCompleted > 0) {
      handicapIndex = finaleRound?.handicap;
      courseHandicap = finaleRound?.courseHandicap;
      teeboxColor = finaleRound?.teeboxColor
        || finaleConfig?.adminTeebox
        || 'geel';
    } else {
      handicapIndex = recentHandicapMap[userId] ?? u?.handicap ?? 0;
      teeboxColor = finaleConfig?.teeboxMode === 'admin'
        ? (finaleConfig?.adminTeebox || 'geel')
        : 'geel';
      courseHandicap = getBergvlietCourseHandicap(handicapIndex, teeboxColor);
    }

    const startingScore = -(courseHandicap + bonusStrokes);

    let grossSoFar = 0;
    let parSoFar = 0;
    let holesCompleted = 0;

    if (finaleRound?.holeData) {
      Object.entries(finaleRound.holeData).forEach(([holeNum, data]) => {
        if (!data?.strokes) return;
        const hole = bergvlietHoles.find((h) => h.number === parseInt(holeNum, 10));
        if (!hole) return;
        grossSoFar += data.strokes;
        parSoFar += hole.par;
        holesCompleted++;
      });
    }

    const runningOverUnder = grossSoFar - parSoFar;
    const currentScore = startingScore + runningOverUnder;

    return {
      userId,
      name: getUserName?.(userId) || u?.name || 'Unknown',
      handicap: handicapIndex,
      courseHandicap,
      bonusStrokes,
      combinedHandicap: courseHandicap + bonusStrokes,
      startingScore,
      currentScore,
      holesCompleted,
      grossSoFar,
      parSoFar,
      runningOverUnder,
      hasStarted: holesCompleted > 0,
      isNonParticipant: nonParticipants.includes(userId),
    };
  });

  computed.sort((a, b) => {
    if (a.isNonParticipant !== b.isNonParticipant) return a.isNonParticipant ? 1 : -1;
    return a.currentScore - b.currentScore;
  });

  return computed;
}

export function formatFinaleScore(score) {
  if (score === 0) return 'E';
  if (score > 0) return `+${score}`;
  return `${score}`;
}

export function formatTodayScore(overUnder, holesCompleted) {
  if (holesCompleted === 0) return '-';
  if (overUnder === 0) return 'E';
  if (overUnder > 0) return `+${overUnder}`;
  return `${overUnder}`;
}

export function getScoreColor(score, holesCompleted) {
  if (holesCompleted === 0) return '#9ca3af';
  if (score < 0) return '#10b981';
  if (score === 0) return '#ffffff';
  return '#ef4444';
}
